import { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import { customerService } from '../../services/customerService';
import type {
  CustomFieldDefinition,
  CustomerCustomFieldValue,
  CustomFieldFormData
} from '../../types/customFields';
import {
  extractFieldValue,
  validateFieldValue,
  isNumericFieldType,
  isDateFieldType,
  isSelectFieldType
} from '../../types/customFields';
import { Save, X, AlertCircle } from 'lucide-react';

interface CustomFieldsFormProps {
  customerId: number;
  onSave?: () => void;
  onCancel?: () => void;
  readOnly?: boolean;
  showActions?: boolean;
}

export default function CustomFieldsForm({
  customerId,
  onSave,
  onCancel,
  readOnly = false,
  showActions = true
}: CustomFieldsFormProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { showToast } = useToast();

  const [fieldDefinitions, setFieldDefinitions] = useState<CustomFieldDefinition[]>([]);
  const [fieldValues, setFieldValues] = useState<CustomerCustomFieldValue[]>([]);
  const [formData, setFormData] = useState<CustomFieldFormData>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [customerId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load field definitions
      console.log('CustomFieldsForm: Loading field definitions for customer', customerId);
      const defsResponse = await customerService.getAllFieldDefinitions({ is_visible: true });
      console.log('CustomFieldsForm: Field definitions response:', defsResponse);
      const definitions = defsResponse.data || [];
      console.log('CustomFieldsForm: Found', definitions.length, 'field definitions');
      setFieldDefinitions(definitions);

      // Load current values
      const valuesResponse = await customerService.getCustomerFieldValues(customerId, {
        include_definitions: true
      });
      console.log('CustomFieldsForm: Field values response:', valuesResponse);
      const values = Array.isArray(valuesResponse) ? valuesResponse : (valuesResponse?.data || []);
      console.log('CustomFieldsForm: Field values array:', values);
      setFieldValues(values);

      // Build initial form data
      const initialData: CustomFieldFormData = {};
      definitions.forEach(def => {
        const existingValue = values.find(v => v.field_definition_id === def.id);
        if (existingValue) {
          initialData[def.field_name] = extractFieldValue(existingValue, def.field_type);
        } else if (def.default_value) {
          initialData[def.field_name] = def.default_value;
        }
      });
      setFormData(initialData);
    } catch (error) {
      console.error('Error loading custom fields:', error);
      showToast('Failed to load custom fields', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName: string, value: any, definition: CustomFieldDefinition) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));

    // Validate on change
    const error = validateFieldValue(value, definition);
    setErrors(prev => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[fieldName] = error;
      } else {
        delete newErrors[fieldName];
      }
      return newErrors;
    });
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    // Validate all fields
    const newErrors: Record<string, string> = {};
    fieldDefinitions.forEach(def => {
      const value = formData[def.field_name];
      const error = validateFieldValue(value, def);
      if (error) {
        newErrors[def.field_name] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showToast('Please fix validation errors', 'error');
      return;
    }

    setSaving(true);
    try {
      // Prepare values for bulk save
      const values = fieldDefinitions
        .filter(def => formData[def.field_name] !== undefined && formData[def.field_name] !== null && formData[def.field_name] !== '')
        .map(def => ({
          field_definition_id: def.id,
          value: formData[def.field_name]
        }));

      if (values.length > 0) {
        await customerService.bulkSetCustomFieldValues(customerId, values);
        showToast('Custom fields saved successfully', 'success');
        if (onSave) onSave();
      } else {
        showToast('No fields to save', 'info');
      }
    } catch (error: any) {
      console.error('Error saving custom fields:', error);
      showToast(error.response?.data?.message || 'Failed to save custom fields', 'error');
    } finally {
      setSaving(false);
    }
  };

  const renderFieldInput = (definition: CustomFieldDefinition) => {
    const value = formData[definition.field_name] ?? '';
    const error = errors[definition.field_name];
    const options = definition.field_options || {};

    const baseInputClasses = `w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 ${
      error ? 'border-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
    } ${
      isDark
        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
    } ${readOnly ? 'opacity-50 cursor-not-allowed' : ''}`;

    switch (definition.field_type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'url':
        return (
          <input
            type={definition.field_type === 'email' ? 'email' : definition.field_type === 'url' ? 'url' : 'text'}
            value={value}
            onChange={(e) => handleFieldChange(definition.field_name, e.target.value, definition)}
            placeholder={options.placeholder}
            disabled={readOnly}
            className={baseInputClasses}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(definition.field_name, e.target.value, definition)}
            placeholder={options.placeholder}
            rows={options.rows || 3}
            disabled={readOnly}
            className={`${baseInputClasses} resize-none`}
          />
        );

      case 'number':
      case 'decimal':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(definition.field_name, e.target.value, definition)}
            placeholder={options.placeholder}
            min={options.min}
            max={options.max}
            step={options.step || (definition.field_type === 'decimal' ? '0.01' : '1')}
            disabled={readOnly}
            className={baseInputClasses}
          />
        );

      case 'currency':
        return (
          <div className="relative">
            {options.prefix && (
              <span className={`absolute left-3 top-2.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {options.prefix}
              </span>
            )}
            <input
              type="number"
              value={value}
              onChange={(e) => handleFieldChange(definition.field_name, e.target.value, definition)}
              placeholder={options.placeholder}
              min={options.min}
              max={options.max}
              step="0.01"
              disabled={readOnly}
              className={`${baseInputClasses} ${options.prefix ? 'pl-8' : ''}`}
            />
          </div>
        );

      case 'percentage':
        return (
          <div className="relative">
            <input
              type="number"
              value={value}
              onChange={(e) => handleFieldChange(definition.field_name, e.target.value, definition)}
              placeholder={options.placeholder}
              min={options.min || 0}
              max={options.max || 100}
              step={options.step || 1}
              disabled={readOnly}
              className={`${baseInputClasses} pr-8`}
            />
            <span className={`absolute right-3 top-2.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {options.suffix || '%'}
            </span>
          </div>
        );

      case 'date':
        return (
          <input
            type="date"
            value={value ? new Date(value).toISOString().split('T')[0] : ''}
            onChange={(e) => handleFieldChange(definition.field_name, e.target.value, definition)}
            min={options.min_date}
            max={options.max_date}
            disabled={readOnly}
            className={baseInputClasses}
          />
        );

      case 'datetime':
        return (
          <input
            type="datetime-local"
            value={value ? new Date(value).toISOString().slice(0, 16) : ''}
            onChange={(e) => handleFieldChange(definition.field_name, e.target.value, definition)}
            disabled={readOnly}
            className={baseInputClasses}
          />
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleFieldChange(definition.field_name, e.target.value, definition)}
            disabled={readOnly}
            className={baseInputClasses}
          >
            <option value="">-- Select --</option>
            {options.choices?.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
        );

      case 'multiselect':
        return (
          <select
            multiple
            value={Array.isArray(value) ? value : []}
            onChange={(e) => {
              const selectedValues = Array.from(e.target.selectedOptions, option => option.value);
              handleFieldChange(definition.field_name, selectedValues, definition);
            }}
            disabled={readOnly}
            className={`${baseInputClasses} h-32`}
          >
            {options.choices?.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {options.choices?.map((choice) => (
              <label key={choice.value} className="flex items-center gap-2">
                <input
                  type="radio"
                  name={definition.field_name}
                  value={choice.value}
                  checked={value === choice.value}
                  onChange={(e) => handleFieldChange(definition.field_name, e.target.value, definition)}
                  disabled={readOnly}
                  className="rounded-full"
                />
                <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {choice.label}
                </span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        return (
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleFieldChange(definition.field_name, e.target.checked, definition)}
              disabled={readOnly}
              className="rounded"
            />
            <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {options.help_text || 'Yes'}
            </span>
          </label>
        );

      case 'checkboxgroup':
        return (
          <div className="space-y-2">
            {options.choices?.map((choice) => (
              <label key={choice.value} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  value={choice.value}
                  checked={Array.isArray(value) && value.includes(choice.value)}
                  onChange={(e) => {
                    const currentValues = Array.isArray(value) ? value : [];
                    const newValues = e.target.checked
                      ? [...currentValues, choice.value]
                      : currentValues.filter(v => v !== choice.value);
                    handleFieldChange(definition.field_name, newValues, definition);
                  }}
                  disabled={readOnly}
                  className="rounded"
                />
                <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {choice.label}
                </span>
              </label>
            ))}
          </div>
        );

      case 'rating':
        const maxRating = options.max_rating || 5;
        return (
          <div className="flex gap-1">
            {Array.from({ length: maxRating }, (_, i) => i + 1).map((rating) => (
              <button
                key={rating}
                type="button"
                onClick={() => !readOnly && handleFieldChange(definition.field_name, rating, definition)}
                disabled={readOnly}
                className={`text-2xl ${
                  rating <= (value || 0) ? 'text-yellow-400' : isDark ? 'text-gray-600' : 'text-gray-300'
                } ${readOnly ? 'cursor-not-allowed' : 'hover:text-yellow-300 cursor-pointer'}`}
              >
                ★
              </button>
            ))}
          </div>
        );

      case 'color':
        return (
          <div className="flex gap-2 items-center">
            <input
              type="color"
              value={value || '#000000'}
              onChange={(e) => handleFieldChange(definition.field_name, e.target.value, definition)}
              disabled={readOnly}
              className="h-10 w-20 rounded cursor-pointer"
            />
            <input
              type="text"
              value={value || ''}
              onChange={(e) => handleFieldChange(definition.field_name, e.target.value, definition)}
              placeholder="#000000"
              disabled={readOnly}
              className={`flex-1 ${baseInputClasses}`}
            />
          </div>
        );

      case 'file':
        return (
          <div>
            <input
              type="file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  // TODO: Handle file upload - store file URL
                  handleFieldChange(definition.field_name, file.name, definition);
                }
              }}
              disabled={readOnly}
              className={baseInputClasses}
            />
            {value && (
              <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Current: {value}
              </p>
            )}
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(definition.field_name, e.target.value, definition)}
            placeholder={options.placeholder}
            disabled={readOnly}
            className={baseInputClasses}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (fieldDefinitions.length === 0) {
    return (
      <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        <p>No custom fields have been defined yet.</p>
      </div>
    );
  }

  // Group fields by field_group
  const groupedFields: Record<string, CustomFieldDefinition[]> = {};
  fieldDefinitions.forEach(def => {
    const group = def.field_group || 'General';
    if (!groupedFields[group]) {
      groupedFields[group] = [];
    }
    groupedFields[group].push(def);
  });

  // Sort fields within each group by display_order
  Object.keys(groupedFields).forEach(group => {
    groupedFields[group].sort((a, b) => a.display_order - b.display_order);
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {Object.entries(groupedFields).map(([group, fields]) => (
        <div key={group} className="space-y-4">
          {/* Group Header */}
          <h4 className={`text-sm font-semibold uppercase tracking-wide ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {group}
          </h4>

          {/* Fields in Group */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map((definition) => (
              <div key={definition.id} className={definition.field_type === 'textarea' ? 'md:col-span-2' : ''}>
                <label className={`block text-sm font-medium mb-2 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  {definition.field_label}
                  {definition.is_required && <span className="text-red-500 ml-1">*</span>}
                </label>

                {renderFieldInput(definition)}

                {/* Help Text */}
                {definition.field_options?.help_text && definition.field_type !== 'checkbox' && (
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {definition.field_options.help_text}
                  </p>
                )}

                {/* Error Message */}
                {errors[definition.field_name] && (
                  <div className="flex items-center gap-1 mt-1 text-red-500 text-xs">
                    <AlertCircle className="h-3 w-3" />
                    <span>{errors[definition.field_name]}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Action Buttons */}
      {showActions && !readOnly && (
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className={`px-6 py-2 rounded-lg border transition-colors ${
                isDark
                  ? 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700'
                  : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
              }`}
            >
              <X className="h-4 w-4 inline mr-2" />
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={saving || Object.keys(errors).length > 0}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Custom Fields'}
          </button>
        </div>
      )}
    </form>
  );
}
